import { mergeAttributes, Node, nodeInputRule } from '@tiptap/react';

export interface ImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, unknown>;
}

type SetImageOptions = {
  src: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  style?: string;
};

// Not keyed `image`: @tiptap/extension-image (used by shared's HTML conversion) declares
// `Commands.image.setImage` without width/height/style, and a second `image` key must match it exactly.
declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    resizableImage: {
      setImage: (options: SetImageOptions) => ReturnType;
    };
  }
}

export const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;

const mergeDimensionsIntoStyle = (style = '', height?: string | number | null): string => {
  let newStyle = style.trim();

  if (height) {
    if (!newStyle.includes('height:')) {
      newStyle = `${newStyle}${newStyle ? '; ' : ''}height: ${height}px`;
    }
  }

  return newStyle;
};

export const Image = Node.create<ImageOptions>({
  name: 'image',

  addOptions() {
    return {
      inline: true,
      allowBase64: false,
      HTMLAttributes: {}
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null
      },
      alt: {
        default: null
      },
      title: {
        default: null
      },
      height: {
        default: null,
        parseHTML: element => element.getAttribute('height')
      },
      style: {
        default: null,
        parseHTML: element => {
          const style = element.getAttribute('style') || '';
          const height = element.getAttribute('height');

          return mergeDimensionsIntoStyle(style, height);
        }
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])',
        getAttrs: element => {
          if (typeof element === 'string') return {};

          const height = element.getAttribute('height');
          const style = element.getAttribute('style') || '';

          return {
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
            style: mergeDimensionsIntoStyle(style, height)
          };
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes };

    if (attrs.height) {
      attrs.style = mergeDimensionsIntoStyle(attrs.style || '', attrs.height);
      delete attrs.height;
    }

    return ['img', mergeAttributes(this.options.HTMLAttributes, attrs)];
  },

  addCommands() {
    return {
      setImage:
        (options: SetImageOptions) =>
        ({ commands }) => {
          const attrs = { ...options };

          if (attrs.height) {
            attrs.style = mergeDimensionsIntoStyle(attrs.style || '', attrs.height);
            delete attrs.height;
          }

          return commands.insertContent({
            type: this.name,
            attrs
          });
        }
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => {
          const [, , src] = match;
          return { src };
        }
      })
    ];
  }
});
